import {useEffect, useRef, useState} from 'react'
import {Animated, StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {Trans, useLingui} from '@lingui/react/macro'

import {getVideoThumbnail} from '#/lib/media/util'
import {atoms as a} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {CapsuleAudioControl} from '#/components/capsules/CapsuleAudioControl'
import {GameController_Stroke2_Corner0_Rounded as GameController} from '#/components/icons/GameController'
import {Text} from '#/components/Typography'
import {IS_WEB} from '#/env'
import {type CapsuleModel} from '#/types/capsule'
import {type FeedVideo} from '../types'
import {
  CapsuleOverlay,
  type CapsuleOverlayMode,
  type CapsuleSkipStage,
} from './CapsuleOverlay'
import {type SeekDelta,TransportLayer} from './TransportLayer'
import {VideoLoadingOverlay} from './VideoLoadingOverlay'

/**
 * Presentation of the ACTIVE capsule page: gesture/transport layer, the
 * capsule front overlay (mode A/B/ended — existing semantics), the Play CTA,
 * progress bar, the shared quiz volume control and the first-run guidance.
 * Pure presentational: every value and callback comes from the controller;
 * nothing here owns app state.
 *
 * Ownership map (capsule player):
 *  - engine.ts                  media element/player + platform seam
 *  - controller.ts              feed/index/mode/navigation policy + actions
 *  - components/PageChrome.tsx  active-page chrome presentation (this file)
 *  - index.tsx                  thin shell: layout, pager, prompt/quiz, arrows
 */

export function PageChrome({
  item,
  overlayMode,
  capsuleModel,
  videoPaused,
  hintSeconds,
  autoAdvancePossible,
  skipStage,
  skipCountdown,
  skipProgress,
  progress,
  videoReady,
  insetsTop,
  onSeek,
  onTogglePause,
  onLongPressChange,
  onPlayFromEnded,
  onSkip,
  onPressPlayCapsule,
  onAudioStateChange,
  displayMuted,
  onLikeCelebrated,
}: {
  item: FeedVideo
  overlayMode: CapsuleOverlayMode
  capsuleModel: CapsuleModel | null
  videoPaused: boolean
  hintSeconds: number | null
  autoAdvancePossible: boolean
  skipStage: CapsuleSkipStage
  skipCountdown: number
  skipProgress: number
  progress: number
  /** False while the video cannot play yet — the loading overlay is shown. */
  videoReady: boolean
  insetsTop: number
  onSeek: (delta: SeekDelta) => void
  onTogglePause: () => void
  onLongPressChange: (active: boolean) => void
  onPlayFromEnded: () => void
  onSkip: () => void
  onPressPlayCapsule: () => void
  onAudioStateChange: (state: {muted: boolean; volume: number}) => void
  /** True while the page's audio is off: the web start-muted policy OR a
      global mute — the floating volume toggle reflects the real element
      state, not the bare master state. */
  displayMuted: boolean
  /** A Like was added on this capsule. Tap position in page coordinates, for
      the flying-hearts celebration rendered by the screen shell. */
  onLikeCelebrated?: (x: number, y: number) => void
}) {
  const {t: l} = useLingui()
  const post = item.post
  const capsuleDescription =
    (item.capsule?.description as string | undefined) ?? ''

  return (
    <View style={[StyleSheet.absoluteFill]}>
      {/* Transport + gestures sit under the chrome (z 10) — chrome controls
          win every tap; taps that fall through reach the gesture surface. */}
      <TransportLayer
        videoPaused={videoPaused}
        onTogglePause={onTogglePause}
        onSeek={onSeek}
        onLongPressChange={onLongPressChange}
      />

      {/* Loading overlay — blurred first-frame thumbnail + white spinner,
          shown while the video cannot play yet (z15: above the transport
          layer, below the capsule front). */}
      {!videoReady && <VideoLoadingOverlay thumbnail={getVideoThumbnail(item.video)} />}

      {/* Capsule front / playing / ended — existing overlay semantics. */}
      <CapsuleOverlay
        post={post}
        capsule={capsuleModel}
        capsuleDescription={capsuleDescription}
        mode={overlayMode}
        feedContext={item.feedContext}
        onPlayFromEnded={onPlayFromEnded}
        hintSeconds={hintSeconds}
        canAutoAdvance={autoAdvancePossible}
        skipStage={skipStage}
        skipCountdown={skipCountdown}
        skipProgress={skipProgress}
        onSkip={onSkip}
        onLikeCelebrated={onLikeCelebrated}
      />

      {/* Play call-to-action — scroll presentation only. It commits to the
          capsule (unmute, keep playing) instead of opening the quiz. */}
      {overlayMode === 'scroll' && (
        <View style={[a.absolute, {bottom: 108, right: 16}, a.z_50]}>
          <Button
            label={l`Play`}
            accessibilityHint={l`Plays this capsule`}
            onPress={onPressPlayCapsule}
            size="large"
            variant="solid"
            color="primary"
            style={[a.rounded_full, {paddingHorizontal: 20}]}>
            <ButtonIcon icon={GameController} position="left" />
            <ButtonText style={[a.font_bold]}>
              <Trans>Play</Trans>
            </ButtonText>
          </Button>
        </View>
      )}

      {/* Playback progress bar — top of the video (both presentations). */}
      <View
        pointerEvents="none"
        style={[a.absolute, a.z_40, {top: 0, left: 0, right: 0, height: 3}]}>
        <View style={[a.flex_1, {backgroundColor: 'rgba(255,255,255,0.25)'}]} />
        <View
          style={[
            a.absolute,
            {top: 0, bottom: 0, left: 0},
            {width: `${progress * 100}%`},
            {backgroundColor: '#0085ff'},
          ]}
        />
      </View>

      {/* Shared capsule volume control (same component + state as the quiz
          play pages) — top right of the video. Its icon follows the real
          element state (start-muted in scroll mode) until the user acts. */}
      <CapsuleAudioControl
        primaryColor="#0085ff"
        buttonTop={insetsTop + 16}
        buttonRight={16}
        displayMuted={displayMuted}
        onStateChange={onAudioStateChange}
      />

      {/* First-run guidance — shows once per visit, fades itself away. */}
      <FirstRunHint />
    </View>
  )
}

/**
 * Ready front for pages in the ±1 window around the active page: the capsule
 * front (avatar, description, actions, reply bar) is ALREADY rendered while
 * the page is still off-screen, so a scroll to the neighbor shows a complete
 * page — data and buttons included — instead of a bare video whose chrome
 * pops in only after the pager commits. The overlay is functional (like,
 * reply, etc. work on the neighbor while it slides in); the Play CTA,
 * transport, progress and audio control stay active-page-only.
 */
export function AdjacentPageFront({item}: {item: FeedVideo}) {
  const capsuleDescription =
    (item.capsule?.description as string | undefined) ?? ''
  return (
    <View style={[StyleSheet.absoluteFill]} pointerEvents="box-none">
      <CapsuleOverlay
        post={item.post}
        capsule={null}
        capsuleDescription={capsuleDescription}
        mode="scroll"
        feedContext={item.feedContext}
        onPlayFromEnded={() => {}}
        hintSeconds={null}
        canAutoAdvance={false}
        skipStage="hidden"
        skipCountdown={0}
        skipProgress={0}
        onSkip={() => {}}
      />
    </View>
  )
}

/**
 * First-run guidance — a short, non-blocking pill over the video that tells
 * a new visitor how the surface works, then fades away on its own. Shows once
 * per app visit (module flag), so it never haunts returning users.
 */
let firstRunHintShown = false
function FirstRunHint() {
  const {t: l} = useLingui()
  const opacity = useRef(new Animated.Value(0)).current
  // Rendered only for the first page of the visit: the module flag is read
  // once at mount (a re-render of the SAME instance must not hide the pill).
  const [visible, setVisible] = useState(() => !firstRunHintShown)
  useEffect(() => {
    if (!visible) return
    firstRunHintShown = true
    Animated.timing(opacity, {
      toValue: 1,
      duration: 300,
      useNativeDriver: true,
    }).start()
    const id = setTimeout(() => {
      Animated.timing(opacity, {
        toValue: 0,
        duration: 500,
        useNativeDriver: true,
      }).start(({finished}) => {
        // Remove the (now invisible) pill from the DOM and the a11y tree.
        if (finished) setVisible(false)
      })
    }, 4200)
    return () => clearTimeout(id)
  }, [opacity, visible])

  if (!visible) return null

  return (
    <Animated.View
      pointerEvents="none"
      style={[
        a.absolute,
        {top: '15%', left: 0, right: 0, zIndex: 45, alignItems: 'center'},
        {opacity},
      ]}>
      <View
        style={{
          backgroundColor: 'rgba(0,0,0,0.55)',
          borderRadius: 999,
          borderWidth: 1,
          borderColor: 'rgba(255,255,255,0.25)',
          paddingHorizontal: 18,
          paddingVertical: 10,
        }}>
        <Text
          style={[
            a.text_sm,
            a.font_semi_bold,
            {color: '#fff', textAlign: 'center'},
          ]}>
          {IS_WEB
            ? l`Scroll or use the arrows for the next capsule — tap the video for controls`
            : l`Swipe up for the next capsule — tap the video for controls`}
        </Text>
      </View>
    </Animated.View>
  )
}

/**
 * Stand-in page for capsules outside the ±1 playback window: the thumbnail
 * over black. The pager stays fully scrollable with only three real video
 * elements instead of one per feed capsule.
 */
export function PosterPage({thumbnail}: {thumbnail?: string}) {
  return (
    <View style={[StyleSheet.absoluteFill, {backgroundColor: '#000'}]}>
      {thumbnail ? (
        <Image
          source={{uri: thumbnail}}
          style={[StyleSheet.absoluteFill]}
          contentFit="cover"
          accessibilityIgnoresInvertColors
          transition={150}
        />
      ) : null}
    </View>
  )
}
